import { Badge } from '@/components/ui/badge';
import { Shield, Send, CheckCircle, Eye, EyeOff, AlertOctagon, Mail as MailIcon, UserMinus, Flag, Ban, Clock, XCircle, AlertTriangle, MousePointerClick } from 'lucide-react';

export interface EmailRowData {
  email: string;
  status: string;
  ub_status: string | null;
  domain?: string | null;
  last_activity: string;
  has_usebouncer: boolean;
  has_brevo: boolean;
  ub_id: string | null;
  brevo_delivered: number;
  brevo_opened: number;
  brevo_hard_bounced: number;
  brevo_soft_bounced: number;
  brevo_sent: number;
  brevo_unsubscribed: number;
  brevo_complained: number;
  brevo_blocked: number;
  brevo_deferred: number;
  projects: string[];
  marketing_blocked: boolean;
  transactional_blocked: boolean;
}

export function SourceIcons({ row }: { row: Pick<EmailRowData, 'has_usebouncer' | 'has_brevo'> }) {
  return (
    <span className="inline-flex items-center gap-1">
      {row.has_usebouncer && (
        <span title="UseBouncer"><Shield className="h-3.5 w-3.5 text-blue-600" /></span>
      )}
      {row.has_brevo && (
        <span title="Delivery"><Send className="h-3.5 w-3.5 text-orange-500" /></span>
      )}
    </span>
  );
}

export function BrevoIcons({ row }: { row: Pick<EmailRowData, 'brevo_sent' | 'brevo_delivered' | 'brevo_opened' | 'brevo_hard_bounced' | 'brevo_soft_bounced' | 'brevo_unsubscribed' | 'brevo_complained' | 'brevo_blocked' | 'brevo_deferred'> }) {
  const bounced = row.brevo_hard_bounced + row.brevo_soft_bounced;
  const items = [
    { value: row.brevo_sent, icon: MailIcon, color: 'text-gray-500', title: 'Sent' },
    { value: row.brevo_delivered, icon: CheckCircle, color: 'text-green-600', title: 'Delivered' },
    { value: row.brevo_opened, icon: Eye, color: 'text-blue-600', title: 'Opened' },
    { value: bounced, icon: AlertOctagon, color: 'text-red-600', title: 'Bounced' },
  ];
  const extraItems = [
    { value: row.brevo_unsubscribed, icon: UserMinus, color: 'text-orange-500', title: 'Unsub' },
    { value: row.brevo_complained, icon: Flag, color: 'text-rose-500', title: 'Complained' },
    { value: row.brevo_blocked, icon: Ban, color: 'text-slate-500', title: 'Blocked' },
    { value: row.brevo_deferred, icon: Clock, color: 'text-amber-500', title: 'Deferred' },
  ].filter((i) => i.value > 0);
  return (
    <div className="flex items-center gap-2 text-xs">
      {items.map((item) => {
        const Icon = item.icon;
        const active = item.value > 0;
        return (
          <span
            key={item.title}
            className={`flex items-center gap-0.5 ${active ? item.color : 'text-gray-300'}`}
            title={item.title}
          >
            <Icon className="h-3.5 w-3.5" />
            {item.value}
          </span>
        );
      })}
      {extraItems.map((item) => {
        const Icon = item.icon;
        return (
          <span
            key={item.title}
            className={`flex items-center gap-0.5 ${item.color}`}
            title={item.title}
          >
            <Icon className="h-3.5 w-3.5" />
            {item.value}
          </span>
        );
      })}
    </div>
  );
}

export function formatYesNoUnknown(value: string | null) {
  if (!value) return '-';
  switch (value) {
    case 'yes': return <Badge variant="destructive" className="text-xs">Yes</Badge>;
    case 'no': return <Badge variant="success" className="text-xs">No</Badge>;
    default: return <Badge variant="secondary" className="text-xs">Unknown</Badge>;
  }
}

export function formatYesNoUnknownInverse(value: string | null) {
  if (!value) return '-';
  switch (value) {
    case 'yes': return <Badge variant="success" className="text-xs">Yes</Badge>;
    case 'no': return <Badge variant="secondary" className="text-xs">No</Badge>;
    default: return <Badge variant="secondary" className="text-xs">Unknown</Badge>;
  }
}

export function getStatusBadge(status: string) {
  switch (status) {
    case 'deliverable':
      return <Badge variant="success">deliverable</Badge>;
    case 'risky':
      return <Badge variant="warning">risky</Badge>;
    case 'undeliverable':
      return <Badge variant="destructive">undeliverable</Badge>;
    case 'error':
      return <Badge variant="destructive">error</Badge>;
    default:
      return <Badge variant="secondary">unknown</Badge>;
  }
}

const variantDotColor: Record<string, string> = {
  success: 'bg-emerald-500',
  destructive: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  slate: 'bg-slate-500',
  secondary: 'bg-gray-400',
};

const variantTextColor: Record<string, string> = {
  success: 'text-emerald-700',
  destructive: 'text-red-700',
  warning: 'text-amber-700',
  info: 'text-blue-700',
  purple: 'text-purple-700',
  orange: 'text-orange-700',
  slate: 'text-slate-700',
  secondary: 'text-gray-500',
};

const variantLineColor: Record<string, string> = {
  success: 'border-emerald-300',
  destructive: 'border-red-300',
  warning: 'border-amber-300',
  info: 'border-blue-300',
  purple: 'border-purple-300',
  orange: 'border-orange-300',
  slate: 'border-slate-300',
  secondary: 'border-gray-300',
};

export type EventVariant = 'success' | 'destructive' | 'warning' | 'info' | 'purple' | 'orange' | 'slate' | 'secondary';

export const eventConfig: Record<string, { label: string; variant: EventVariant; icon: typeof Send }> = {
  delivered:     { label: 'Delivered',     variant: 'success',     icon: CheckCircle },
  opened:        { label: 'Opened',        variant: 'info',        icon: Eye },
  uniqueOpened:  { label: 'Opened',        variant: 'info',        icon: Eye },
  unique_opened: { label: 'Opened',        variant: 'info',        icon: Eye },
  proxy_open:    { label: 'Proxy Open',    variant: 'slate',       icon: EyeOff },
  unique_proxy_open: { label: 'Proxy Open', variant: 'slate',      icon: EyeOff },
  loadedByProxy: { label: 'Proxy Open',    variant: 'slate',       icon: EyeOff },
  clicked:       { label: 'Clicked',       variant: 'purple',      icon: MousePointerClick },
  uniqueClicks:  { label: 'Clicked',       variant: 'purple',      icon: MousePointerClick },
  unique_clicks: { label: 'Clicked',       variant: 'purple',      icon: MousePointerClick },
  sent:          { label: 'Sent',          variant: 'secondary',   icon: Send },
  requests:      { label: 'Sent',          variant: 'secondary',   icon: Send },
  hardBounce:    { label: 'Hard Bounce',   variant: 'destructive', icon: XCircle },
  softBounce:    { label: 'Soft Bounce',   variant: 'warning',     icon: AlertTriangle },
  unsubscribed:  { label: 'Unsubscribed',  variant: 'orange',      icon: UserMinus },
  complaint:     { label: 'Complaint',     variant: 'destructive', icon: Flag },
  blocked:       { label: 'Blocked',       variant: 'slate',       icon: Ban },
  deferred:      { label: 'Deferred',      variant: 'warning',     icon: Clock },
};

export function getEventColors(event: string) {
  const cfg = eventConfig[event];
  const variant = cfg?.variant || 'secondary';
  return {
    dot: variantDotColor[variant] || 'bg-gray-400',
    text: variantTextColor[variant] || 'text-gray-500',
    line: variantLineColor[variant] || 'border-gray-300',
    label: cfg?.label || event,
    icon: cfg?.icon || Send,
  };
}

export function getEventBadge(event: string) {
  const cfg = eventConfig[event];
  if (!cfg) {
    return <Badge variant="secondary">{event}</Badge>;
  }
  const Icon = cfg.icon;
  return (
    <Badge variant={cfg.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}
